/**
 * Reconnect — shared exponential backoff + staleness detection
 *
 * Pure utilities for WebSocket reconnection policy.
 * No actual WebSocket code — just the math and state machine.
 */

export interface ReconnectConfig {
  /** Initial delay in ms (default 1000) */
  initialDelayMs?: number;
  /** Max delay cap in ms (default 30000) */
  maxDelayMs?: number;
  /** Backoff multiplier (default 2) */
  multiplier?: number;
  /** Jitter factor 0..1 (default 0.1) */
  jitter?: number;
}

export type ConnectionState = 'connected' | 'reconnecting' | 'disconnected';

export type StalenessLevel = 'fresh' | 'stale' | 'disconnected';

/** Compute next backoff delay with optional jitter */
export function computeBackoff(attempt: number, config?: ReconnectConfig): number {
  const initial = config?.initialDelayMs ?? 1000;
  const max = config?.maxDelayMs ?? 30000;
  const multiplier = config?.multiplier ?? 2;
  const jitterFactor = config?.jitter ?? 0.1;

  const base = Math.min(initial * Math.pow(multiplier, attempt), max);
  const jitterRange = base * jitterFactor;
  const jitterOffset = (Math.random() - 0.5) * 2 * jitterRange;
  return Math.max(0, Math.round(base + jitterOffset));
}

/** Compute deterministic backoff (no jitter) for testing */
export function computeBackoffDeterministic(attempt: number, config?: ReconnectConfig): number {
  const initial = config?.initialDelayMs ?? 1000;
  const max = config?.maxDelayMs ?? 30000;
  const multiplier = config?.multiplier ?? 2;
  return Math.min(initial * Math.pow(multiplier, attempt), max);
}

/** Determine staleness level from last message timestamp */
export function computeStaleness(
  lastMessageMs: number | null,
  nowMs: number,
  staleThresholdMs: number = 15_000,
  disconnectedThresholdMs: number = 60_000
): StalenessLevel {
  if (lastMessageMs === null) return 'disconnected';
  const age = nowMs - lastMessageMs;
  if (age >= disconnectedThresholdMs) return 'disconnected';
  if (age >= staleThresholdMs) return 'stale';
  return 'fresh';
}

/** Reconnection state machine */
export class ReconnectState {
  private _attempt = 0;
  private _state: ConnectionState = 'disconnected';
  private _lastMessageMs: number | null = null;
  private config: Required<ReconnectConfig>;

  constructor(config?: ReconnectConfig) {
    this.config = {
      initialDelayMs: config?.initialDelayMs ?? 1000,
      maxDelayMs: config?.maxDelayMs ?? 30000,
      multiplier: config?.multiplier ?? 2,
      jitter: config?.jitter ?? 0.1,
    };
  }

  get state(): ConnectionState { return this._state; }
  get attempt(): number { return this._attempt; }
  get lastMessageMs(): number | null { return this._lastMessageMs; }

  /** Mark connection as established */
  onConnected(): void {
    this._state = 'connected';
    this._attempt = 0;
    this._lastMessageMs = Date.now();
  }

  /** Mark a message received (updates staleness timer) */
  onMessage(timestampMs?: number): void {
    this._lastMessageMs = timestampMs ?? Date.now();
  }

  /** Mark connection lost, compute next retry delay */
  onDisconnected(): { nextDelayMs: number } {
    this._state = 'reconnecting';
    const delay = computeBackoff(this._attempt, this.config);
    this._attempt++;
    return { nextDelayMs: delay };
  }

  /** Give up reconnecting */
  giveUp(): void {
    this._state = 'disconnected';
  }

  /** Get current staleness level */
  staleness(nowMs?: number): StalenessLevel {
    if (this._state !== 'connected') return 'disconnected';
    return computeStaleness(this._lastMessageMs, nowMs ?? Date.now());
  }

  /** Reset all state */
  reset(): void {
    this._attempt = 0;
    this._state = 'disconnected';
    this._lastMessageMs = null;
  }
}
